import React, { useEffect } from 'react'
import { makeStyles } from '@mui/styles'
import clsx from 'clsx'

import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import BellIcon from '@assets/images/icons/bell.svg'
import useWebSocketEvent, { EventTypes } from '@/modules/webSocket'
import { useDispatch } from 'react-redux'
import { toggleNotificationOverlay } from '@/redux/ducks/menu/main'
import { fetchNewsfeedCount, finishedFetchingNewsfeedCountData } from '@/redux/ducks/newsfeed'
import ThemeInterface from '@/theme-interface'
import { useAppSelector } from '@/redux/store'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  // eslint plugin doesn't recognize animations used in other styles
  // eslint-disable-next-line
  '@keyframes borderSlideIn': {
    from: { transform: 'translate3d(100%, 0, 0)' },
    to: { transform: 'translate3d(0, 0, 0)' },
  },
  bellIcon: {
    position: 'relative',
    '& path': {
      fill: 'var(--color-menu-icon)',
    },
    '&:hover path': {
      fill: theme.primary,
    },
  },
  active: ({
    fill: theme.primary,
    '&:after': {
      content: '""',
      width: 0,
      height: 0,
      borderTop: '10px solid transparent',
      borderBottom: '10px solid transparent',
      borderRight: `10px solid ${theme.backgroundColor}`,
      animationName: '$borderSlideIn',
      animationDuration: '0.5s',
      animationFillMode: 'forwards',
      position: 'absolute',
      right: 0,
      [theme.devices.mobile]: {
        top: 19,
        borderTop: '10px solid transparent',
        borderBottom: `10px solid ${theme.backgroundColor}`,
        borderRight: '10px solid transparent',
        borderLeft: '10px solid transparent',
      },
    },
    '& $bellIcon path': {
      fill: theme.primary,
    },
  }),
  hideActiveIcon: {
    '&:after': {
      display: 'none',
    },
  },
  iconWrapper: {
    position: 'relative',
  },
  badge: {
    height: 20,
    width: 20,
    display: 'block',
    borderRadius: '50%',
    color: theme.white,
    backgroundColor: theme.error,
    fontSize: 10,
    textAlign: 'center',
    lineHeight: '23px',
    position: 'absolute',
    right: 0,
    top: 0,
    transform: 'translate3d(50%, -50%, 0)',
  },
  [theme.devices.mobile]: {
    container: {
      flexDirection: 'row',
      margin: 0,
      width: 'auto',
    },
    iconWrapper: {
      marginRight: 30,
    },
    newsIcon: {
      marginRight: 70,
    },
    bellIcon: {
      height: 20,
    },
  },
}))

interface NotificationButtonProps {
  iconSize?: { width: number, height: number },
  hideActiveIcon?: boolean,
  styles?: { root?: string, badge?: string },
}

const NotificationButton = (props: NotificationButtonProps) => {
  const dispatch = useDispatch()
  const {
    iconSize = { width: 20, height: 20 },
    hideActiveIcon,
    styles = { root: null, badge: null },
  } = props
  const classes = useStyles()
  const loggedInUserId = useAppSelector(state => state.user.info.id)
  const unreadCount = useAppSelector(state => state.newsfeed.unreadCount)
  const notificationOverlayOpen = useAppSelector(state => state.menu.main.notificationOverlayOpen)

  useWebSocketEvent(EventTypes.NOTIFICATION_COUNT, (msg) => {
    const { data: { unreadCount, userId } } = msg
    if (userId && loggedInUserId !== userId) {
      return
    }

    dispatch(finishedFetchingNewsfeedCountData({ unreadCount }))
  })
  useEffect(() => {
    if (unreadCount < 0) {
      dispatch(fetchNewsfeedCount())
    }
  }, [])

  const className = clsx(
    classes.container,
    classes.iconWrapper,
    classes.newsIcon,
    styles?.root && styles.root,
    notificationOverlayOpen && classes.active,
    hideActiveIcon && classes.hideActiveIcon,
  )
  const badgeClassName = clsx(
    classes.badge,
    styles?.badge && styles.badge,
  )

  return (
    <a
      className={className}
      onClick={() => dispatch(toggleNotificationOverlay(!notificationOverlayOpen))}
      data-qs-id='notification-menu-button' >
      <span className={classes.bellIcon}>
        {unreadCount > 0 && <span className={badgeClassName}>{unreadCount}</span>}
        <SVGIcon
          width={`${iconSize.width}px`}
          height={`${iconSize.height}px`}
          svg={BellIcon}
        />
      </span>
    </a>
  )
}

export default NotificationButton
